'use client';

import type { ComponentType, ReactNode } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import IconButton from '@savvy-kit/components/icon-button';
import { cn } from '@savvy-kit/lib/utils';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { DragHandle } from 'src/components/shared/reorder/drag-handle';

type SortableRowProps = {
  readonly id: string;
  readonly label: string;
  /** Second line under the label; the Tabs pane has none. */
  readonly description?: string;
  readonly icon?: ComponentType<{ readonly className?: string }>;
  /** A per-row control (e.g. a hide/show `Switch`) rendered at the trailing edge. */
  readonly trailing?: ReactNode;
  readonly className?: string;
  readonly index: number;
  readonly count: number;
  readonly onMove: (from: number, to: number) => void;
};

export function SortableRow({
  id,
  label,
  description,
  icon: Icon,
  trailing,
  className,
  index,
  count,
  onMove,
}: SortableRowProps) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  const isFirst = index === 0;
  const isLast = index === count - 1;

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn('flex items-center gap-3 transition-opacity', isDragging && 'opacity-50', className)}
    >
      <DragHandle
        ref={setActivatorNodeRef}
        attributes={attributes}
        listeners={listeners}
        label={`Drag to reorder ${label}`}
        className="size-6 text-tertiary hover:text-secondary"
      />
      {Icon !== undefined && <Icon className="size-5 shrink-0 text-secondary" />}
      <div className="min-w-0 flex-1">
        <div
          data-slot="reorder-row-label"
          className={cn(
            'truncate text-sm text-dark',
            description === undefined ? 'font-medium' : 'font-semibold leading-tight',
          )}
        >
          {label}
        </div>
        {description !== undefined && <div className="mt-0.5 text-xs text-secondary">{description}</div>}
      </div>
      {/* The prototype's rows carry a grip and nothing else, and dragging is the only affordance a
          pointer needs. These stay collapsed (not `hidden` — that would drop them from the tab
          order) until a keyboard reaches them, which is the only path dnd-kit leaves unserved.
          Zero width, not just transparent: laid out but invisible, they narrow the label column
          enough to wrap the longest section caption. */}
      <div className="flex w-0 shrink-0 items-center gap-1 overflow-hidden opacity-0 transition-[width,opacity] focus-within:w-auto focus-within:overflow-visible focus-within:opacity-100">
        {/* `aria-disabled`, not `disabled`: the move that lands a row at an end disables the very
            button the keyboard user just pressed, and the browser blurs it to <body> mid-reorder. */}
        <IconButton
          variant="ghost"
          size="xs"
          aria-label={`Move ${label} up`}
          aria-disabled={isFirst}
          className={cn(isFirst && 'opacity-50')}
          onClick={() => {
            if (!isFirst) onMove(index, index - 1);
          }}
        >
          <ChevronUp className="size-4 text-secondary" aria-hidden="true" />
        </IconButton>
        <IconButton
          variant="ghost"
          size="xs"
          aria-label={`Move ${label} down`}
          aria-disabled={isLast}
          className={cn(isLast && 'opacity-50')}
          onClick={() => {
            if (!isLast) onMove(index, index + 1);
          }}
        >
          <ChevronDown className="size-4 text-secondary" aria-hidden="true" />
        </IconButton>
      </div>
      {trailing}
    </div>
  );
}
